import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { API_URL_COMPANIES } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';
import { DashboardCardRaw, DashboardFilter, Report } from '../shared/models';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private asyncRequestService: AsyncRequestService,
    private errorHandlingService: ErrorHandlingService,
    private httpClient: HttpClient
  ) {}

  postForDashboard(dashboardFilter: DashboardFilter): Observable<DashboardCardRaw[]> {
    return this.asyncRequestService
      .post<DashboardCardRaw[]>(
        `${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports/dashboard`,
        dashboardFilter
      )
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getMetricsReportsList(): Observable<Report[]> {
    return this.httpClient
      .get<Report[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getMetricsReport(reportId: number): Observable<Report> {
    return this.httpClient
      .get<Report>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports/${reportId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  getVerbatimReportsList(): Observable<Report[]> {
    return this.httpClient
      .get<Report[]>(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/verbatim/reports`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteMetricReport(reportId: number) {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports/${reportId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  deleteVerbatimReport(reportId: number) {
    return this.httpClient
      .delete(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/verbatim/reports/${reportId}`)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updateMetricReport(report: Report) {
    return this.httpClient
      .put(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports/${report.id}`, report)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  updateVerbatimReport(report: Report) {
    return this.httpClient
      .put(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/verbatim/reports/${report.id}`, report)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createMetricReport(report: Report) {
    return this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/metrics-reports`, report)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  createVerbatimReport(report: Report) {
    return this.httpClient
      .post(`${API_URL_COMPANIES}/${this.alcmeonContextService.companyId}/verbatim/reports`, report)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * Hardcodes the list of periodicity offered by the UI,
   * and converts each of them to a tuple (when, duration) as understood by the backend
   */
  getPeriodicityFrontEndToBackend(): { key: string; when: string; duration: string }[] {
    return [
      {
        key: 'yesterday',
        when: 'first_hour_of_day',
        duration: 'day',
      },
      {
        key: 'calendar_week',
        when: 'first_day_of_week',
        duration: 'week',
      },
      {
        key: 'last_7_days',
        when: 'first_hour_of_day',
        duration: 'week',
      },
      {
        key: 'current_week',
        when: 'current_week',
        duration: 'week',
      },
      {
        key: 'calendar_month',
        when: 'first_day_of_month',
        duration: 'month',
      },
      {
        key: 'last_30_days',
        when: 'first_hour_of_day',
        duration: 'month',
      },
      {
        key: 'current_month',
        when: 'current_month',
        duration: 'month',
      },
      {
        key: 'quarter',
        when: 'first_day_of_quarter',
        duration: 'quarter',
      },
      {
        key: 'year',
        when: 'first_day_of_year',
        duration: 'year',
      },
    ];
  }
}
